import type {
  ConstructionSite,
  CreateConstructionSiteIntakeCommand,
  UpdateConstructionSiteCommand,
} from "@/domain/model/ConstructionSite";
import type { ConstructionSiteService } from "@/domain/port/ConstructionSiteService";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type {
  ConstructionSiteDto,
  CreateConstructionSiteCommand,
} from "./dto/ConstructionSiteDto";
import {
  constructionSiteFromDto,
  constructionSiteIntakeRequestFromCommand,
  updateConstructionSiteRequestFromCommand,
} from "./mappers/ConstructionSiteMapper";
import type { ConstructionSiteId } from "@/domain/model/Ids";

const BATCH_LIMIT = 100;

export class ConstructionSiteServiceImpl implements ConstructionSiteService {
  constructor(private readonly http: HttpClient) {}

  async list(
    q: PageQuery,
  ): Promise<{ items: ConstructionSite[]; total: number }> {
    const params = toQueryParams(q);

    const { items, total } = await this.http.request<{
      items: ConstructionSiteDto[];
      total: number;
    }>(`/api/construction-sites?${params.toString()}`);

    return { items: items.map(constructionSiteFromDto), total };
  }

  async create(cmd: CreateConstructionSiteCommand): Promise<ConstructionSite> {
    const res = await this.http.request<ConstructionSiteDto>(
      "/api/construction-sites",
      {
        method: "POST",
        body: JSON.stringify(cmd),
      },
    );

    return constructionSiteFromDto(res);
  }

  async getManyByIds(ids: ConstructionSiteId[]): Promise<ConstructionSite[]> {
    if (ids.length === 0) return [];

    // chunk to respect server max
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
      chunks.push(ids.slice(i, i + BATCH_LIMIT));
    }

    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const qs = new URLSearchParams({ ids: chunk.join(",") }); // CSV as per your route
        const dtos = await this.http.request<ConstructionSiteDto[]>(
          `/api/construction-sites/_batch?${qs.toString()}`,
        );
        return dtos.map(constructionSiteFromDto);
      }),
    );

    return results.flat();
  }

  async getById(id: ConstructionSiteId): Promise<ConstructionSite> {
    const dto = await this.http.request<ConstructionSiteDto>(
      `/api/construction-sites/${encodeURIComponent(String(id))}`,
    );
    return constructionSiteFromDto(dto);
  }

  async update(
    id: ConstructionSiteId,
    cmd: UpdateConstructionSiteCommand,
  ): Promise<ConstructionSite> {
    const req = updateConstructionSiteRequestFromCommand(cmd);
    const dto = await this.http.request<ConstructionSiteDto>(
      `/api/construction-sites/${encodeURIComponent(String(id))}`,
      {
        method: "PUT",
        body: JSON.stringify(req),
      },
    );
    return constructionSiteFromDto(dto);
  }

  async delete(id: ConstructionSiteId): Promise<ConstructionSite> {
    const dto = await this.http.request<ConstructionSiteDto>(
      `/api/construction-sites/${encodeURIComponent(String(id))}`,
      { method: "DELETE" },
    );
    return constructionSiteFromDto(dto);
  }

  async createIntake(
    cmd: CreateConstructionSiteIntakeCommand,
  ): Promise<ConstructionSite> {
    const res = await this.http.request<ConstructionSiteDto>(
      "/api/construction-sites/intake",
      {
        method: "POST",
        body: JSON.stringify(constructionSiteIntakeRequestFromCommand(cmd)),
      },
    );

    return constructionSiteFromDto(res);
  }
}

function isNonEmptyString(x: unknown): x is string {
  return typeof x === "string" && x.trim().length > 0;
}

function flattenConstructionSiteFilter(filter: any): Record<string, string> {
  const out: Record<string, string> = {};
  if (isNonEmptyString(filter?.q)) out.q = filter.q.trim();
  if (isNonEmptyString(filter?.clientId)) out.clientId = filter.clientId;
  return out;
}

function toQueryParams(q: PageQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(q.page),
    pageSize: String(q.pageSize),
    sortBy: q.sortBy ?? "updatedAt",
    sortDir: q.sortDir ?? "desc",
  });

  const flat = flattenConstructionSiteFilter(q.filter);
  for (const [k, v] of Object.entries(flat)) params.set(k, v);

  return params;
}
